import {AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {select} from '@angular-redux/store';
import * as R from "ramda";
import {ActionsService} from '../../Redux/Actions/actions.service';
import {ApplicationToolbarActions} from '../../Redux/Actions/application-toolbar-actions';
import {MainViewType, SortDirectionType} from '../../models/view';
import {ActivatedRoute, Router} from '@angular/router';
import {ApplicationCatalogActions} from '../../Redux/Actions/application-catalog-actions';
import {IDesignSolution, IEntityAlert, IProjectTypeSelector} from '../../models/common';
import {ApplicationTestActions} from '../../Redux/Actions/application-tests-actions';
import {isNullOrUndefined} from 'util';
import {MatMenuTrigger} from '@angular/material/menu';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ISpec} from '../../models/ISpec';
import {Observable, Subscription} from "rxjs";
import {map} from "rxjs/operators";
import {Utils} from "../../common/utils";
import {AnalyticsActions} from "../../Redux/Actions/analytics.actions";
import {setWindowTitle} from "../../commonUtil";
import {FeatureEx} from "../../models/features/features";
import {FeatureFilterState} from "../../models/features/feature_const";
import {TTrackingData} from "../../Redux/Middlewares/analytics";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'edsng-main-content',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './main-content.component.html',
  styleUrls: [
    './main-content.component.css',
    './main-content-mobile.component.css',
    './main-content-tablet.component.css'
  ]
})
export class MainContentComponent implements OnInit, OnDestroy, AfterViewInit {

  readonly const_specification = 'specification';
  readonly const_collections = 'collections';
  readonly const_features = 'features';
  readonly const_sorting = 'sorting';

  textSearched: string = '';
  handleObs: Subscription[] = [];

  @select(['appToolBar', 'direction']) direction$;
  @select(['appToolBar', 'sortField']) sortField$;
  @select(['appToolBar', 'viewType']) viewType$;
  @select(['appCatalog', 'appClass']) appClass$;
  @select(['appCatalog', 'loading']) loading$;
  @select(['appCatalog', 'designSolutions']) designSolutions$;
  @select(['appToolBar', 'search']) searchField$;
  @select(['appCatalog', 'selectedSolution']) selectedSolution$;
  @select(['appSpec', 'spec', 'note']) specsNote$: Observable<ISpec<IEntityAlert>>;

  @select(['appSpec', 'spec', 'value']) specsVal$: Observable<ISpec<number>>;
  @select(["appProjectTypes", "breadcrumb"]) breadCrumb$: Observable<IProjectTypeSelector[]>;
  @select(['appSpec', 'featuresFilter']) featuresFilter$: Observable<FeatureEx[]>;

  electricalParams$: Observable<{ vin_min?: number; vin_max?: number; vout?: number; iout?: number; power?: number; }>;
  /**
   * Struttura che viene utilizzata per gestire quale dei menù deve essere aperto in versione mobile
   */
  dialogOpened = {
    specification: false,
    collections: false,
    features: false,
    sorting: false
  };

  speechVisible = true;
  lastItemMenuSelected: IProjectTypeSelector = null;
  menuRootItem = [];
  featureListNo: FeatureEx[] = [];
  featureListYes: FeatureEx[] = [];

  specificationError: boolean = false;
  solutionSelected: boolean = false;

  directionType = SortDirectionType;
  designSolutions: [];

  /**
   * Struttura utilizzata per definire i tipi di ordinamento (quasi tutti sempre presenti)
  */
  sortingMenu = [
    { comparatorKey: 'Default', label: 'GRID.sorting.default', defaultDirectionType: this.directionType.DESCEND },
    { comparatorKey: 'Iout max', label: 'GRID.sorting.ioutMax', defaultDirectionType: this.directionType.ASCEND },
    { comparatorKey: 'Package', label: 'GRID.sorting.package', defaultDirectionType: this.directionType.ASCEND },
    { comparatorKey: 'Part Number', label: 'GRID.sorting.partNumber', defaultDirectionType: this.directionType.ASCEND },
    { comparatorKey: 'Topology', label: 'GRID.sorting.topology', defaultDirectionType: this.directionType.ASCEND },
    { comparatorKey: 'Vin delta', label: 'GRID.sorting.vinDelta', defaultDirectionType: this.directionType.ASCEND },
    { comparatorKey: 'Vin max', label: 'GRID.sorting.vinMax', defaultDirectionType: this.directionType.ASCEND },
    { comparatorKey: 'Vin min', label: 'GRID.sorting.vinMin', defaultDirectionType: this.directionType.DESCEND },
  ];

  @ViewChild('featuresTrigger', {static: false}) featuresTrigger: MatMenuTrigger;
  @ViewChild('specificationTrigger', {static: false}) specificationTrigger: MatMenuTrigger;
  @ViewChild('menuSortTrigger', {static: false}) menuSortTrigger: MatMenuTrigger;

  @HostListener('window:resize') onResize() {
    if (window.innerWidth <= 680) {
      if (this.dialogOpened.specification) {
        this.specificationTrigger.closeMenu();
        this.showSubMenus(this.const_specification);
      }
      if (this.dialogOpened.features) {
        this.featuresTrigger.closeMenu();
        this.showSubMenus(this.const_features);
      }
      if (this.menuSortTrigger && this.menuSortTrigger.menuOpen) {
        this.menuSortTrigger.closeMenu();
        this.showSubMenus(this.const_sorting);
      }
    }

    if (window.innerWidth > 680) {
      if (this.dialogOpened.specification) {
        if (window.innerWidth <= 1024) {
          this.specificationTrigger.openMenu();
        } else {
          this.specificationTrigger.closeMenu();    // in desktop no specification menu available
        }
      }
      if (this.dialogOpened.collections) {
        this.dialogOpened.collections = false;
      }
      if (this.dialogOpened.features) {
        this.featuresTrigger.openMenu();
      }

    }
    this.speechVisible = true;
  }

  constructor(private actSvc: ActionsService, private route: ActivatedRoute, private router: Router, public snack: MatSnackBar, public translate: TranslateService) {
    // console.log("MainContentComponent", new Date().toISOString())
    this.route.params.subscribe(x => {
      setWindowTitle(x['class'], translate);
      this.actSvc.sendAction(
        ApplicationCatalogActions.loadCatalog(
          x['class'],
          x['catalog'],
          isNullOrUndefined(x.filter) ? [] : JSON.parse(x['filter'])
        )
      );
    });

    this.electricalParams$ = this.specsVal$.pipe(
      map(x => ({
        vin_min: Utils.isValueReal(x.input.min) ? x.input.min : undefined,
        vin_max: Utils.isValueReal(x.input.max) ? x.input.max : undefined,
        vout: Utils.isValueReal(x.output.globalOutput.voltage) ? x.output.globalOutput.voltage : undefined,
        iout: Utils.isValueReal(x.output.globalOutput.current) ? x.output.globalOutput.current : undefined,
        power: Utils.isValueReal(x.output.globalOutput.power) ? x.output.globalOutput.power : undefined,
      })));
  }

  ngOnDestroy() {
    this.handleObs.forEach(x => x.unsubscribe());
  }

  sendEvalBoardClick(trackingData: TTrackingData) {
    this.actSvc.sendAction(AnalyticsActions.sendAnalyticsEvent(
      trackingData.hit,
      'redirect',
      {
        cpn: trackingData.cpn,
        rpn: trackingData.rpn ? trackingData.rpn : undefined,
        details: trackingData.details,
        module: trackingData.module,
        topology: trackingData.topology,
        tag: "grid" + (trackingData.tag ? `, ${trackingData.tag}` : '')
      }));
  }
  ngOnInit() {
    this.handleObs.push(this.searchField$.subscribe(x => {
      // console.log('Search main ', x);
      if (x !== this.textSearched) {
        this.textSearched = x;
      }
    }));

    this.handleObs.push(this.selectedSolution$.subscribe(x => {
      if (this.solutionSelected !== (x !== null)) {
        this.solutionSelected = (x !== null);
      }
    }));

    this.handleObs.push(this.breadCrumb$.subscribe(x => {
      if (x.length > 0) {
        this.menuRootItem = [x[0]];
        this.lastItemMenuSelected = x[x.length - 1];
      }
    }));

    this.handleObs.push(this.specsNote$.subscribe(x => {
      const inputError = x.input.max.hasError || x.input.min.hasError;
      const outputError = x.output.outputs.some(y => y.voltage.hasError || y.current.hasError || y.power.hasError);
      const globalOutputError = x.output.globalOutput.power.hasError || x.output.globalOutput.current.hasError || x.output.globalOutput.voltage.hasError;
      const ledError = x.output.ledParam.parallel.hasError || x.output.ledParam.rd.hasError || x.output.ledParam.serial.hasError;

      this.specificationError = inputError || outputError || globalOutputError || ledError;
    }));

    this.handleObs.push(this.featuresFilter$.subscribe(x => {
      this.featureListNo = x.filter((f) => f.stateFilter === FeatureFilterState.NO && !f.isCommon);
      this.featureListYes = x.filter((f) => f.stateFilter === FeatureFilterState.YES && !f.isCommon);
    }));

    this.handleObs.push(this.appClass$.subscribe(x => {
      this.sortByField('Default', SortDirectionType.DESCEND, false);

      if (!R.includes({comparatorKey: 'ID lim'}, this.sortingMenu) && x === 'acdc') {
        this.sortingMenu.push({
          comparatorKey: 'ID lim',
          label: 'GRID.sorting.idLim',
          defaultDirectionType: this.directionType.ASCEND
        });
        this.sortingMenu = R.sortBy(R.compose(R.toLower, R.prop('comparatorKey')))(this.sortingMenu);
      } else {
        const indexToRemove = R.findIndex(R.propEq('comparatorKey', 'ID lim'))(this.sortingMenu);
        if (indexToRemove > -1) {
          this.sortingMenu.splice(indexToRemove, 1);
        }
      }
    }));
  }

  ngAfterViewInit() {
    window.dispatchEvent(new Event('resize'));
  }

  acceptOnlyCharNumber(evt) {
    return (/[a-z_\-\.\d]/i.test(evt.key));
  }

  onTextSearch(evt) {
    this.actSvc.sendAction(ApplicationToolbarActions.setSearch(evt.target.value));
  }

  sortByField(sortStr: string, direction: number, sendAnalyticsEvent: boolean = true) {
    if (sendAnalyticsEvent) {
      this.actSvc.sendAction(AnalyticsActions.sendAnalyticsEvent('sorting_update', 'project', {
        details: {
          field: sortStr,
          sorting: direction === 1 ? 'Asc' : 'Desc'
        }
      }));
    }
    this.actSvc.sendAction(ApplicationToolbarActions.changeSortDirection(direction));
    this.actSvc.sendAction(ApplicationToolbarActions.setSortField(sortStr));
  }

  viewMode(view: MainViewType) {
    this.actSvc.sendAction(ApplicationToolbarActions.setViewType(view));
    this.router.navigate(view === MainViewType.GRID ? ['grid'] : ['list'], {relativeTo: this.route});
  }

  startTest() {
    this.actSvc.sendAction(ApplicationTestActions.testCatalog());
  }

  clearSearch() {
    this.actSvc.sendAction(ApplicationToolbarActions.setSearch(''));
  }

  /**
   * Funzione per gestire quale menù deve essere aperto
   * @param type di menù che deve essere aperto
   */
  showSubMenus(type: any) {
    this.resetDialogOpened();
    this.dialogOpened[type] = true;
  }

  isOneMenuOpened(): boolean {
    return this.dialogOpened.collections || this.dialogOpened.features || this.dialogOpened.specification || this.dialogOpened.sorting;
  }

  /**
   * Funzione che viene utilizzata per nascondere tutti i menù aperti
   */
  resetDialogOpened() {
    this.dialogOpened = {
      specification: false,
      collections: false,
      features: false,
      sorting: false
    };
  }

  collectionSelectItem(item: IProjectTypeSelector) {
    this.lastItemMenuSelected = item;
  }

  applyDialogOpened() {
    if (this.dialogOpened.collections === true) {
      this.dialogOpened.collections = false;
      if (this.lastItemMenuSelected) {
        this.openItemSelected(this.lastItemMenuSelected);
        // this.lastItemMenuSelected = null;
      }
    }
    this.resetDialogOpened();
  }

  /**
   * Funzione richiamata per aprire la collezione selezionata
   * Ripresa da una esistente
   */
  openItemSelected(childSelected: any) {
    if (childSelected && (childSelected.runnable || childSelected.create)) {
      if (isNullOrUndefined(childSelected.url) && !isNullOrUndefined(childSelected.catalog)) {
        this.router.navigate([
          '/app',
          childSelected.classType,
          childSelected.catalog,
          'filter',
          this.paramToString(childSelected.filterTopologies)
        ]);
      } else {
        this.snack.open('Invalid menu entry for ' + childSelected.name, 'OK', {
          duration: 3000
        });
      }
    } else {
      this.snack.open('Invalid menu entry for ' + childSelected.name, 'OK', {
        duration: 3000
      });
    }
  }

  paramToString(obj: any): string {
    return isNullOrUndefined(obj) ? '[]' : JSON.stringify(obj);
  }

  /**
   * funzione utilizzata per evitare che le modal di angular material si chiudano al click
   * @param event
   */
  prevent(event: any) {
    event.stopPropagation();
  }

  closeSpeech() {
    this.speechVisible = false;
  }

  toCPNArray(arr: IDesignSolution[]): string[] {
    return arr.map(x => x.partNumber);
  }

}
